import { Navbar } from "#/feature/navbar/navbar-component";
import { TtsPlayer } from "#/feature/tts-playback/tts-player";
import { authLoadingSignal, connectionErrorSignal } from "#/feature/auth/auth-store";
import { useSignal } from "#/use-signal";
import { useEffect, useRef, type ReactNode } from "react";
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { App as CapacitorNativeApp } from "@capacitor/app";
import { useIsKeyboardVisible } from "./use-keyboard";
import { appStyles, safeAreaBottomStyles, safeAreaTopStyles } from "./capacitor-app.css";

/**
 * Capacitor hardware back: exit on `/` or `/login`, otherwise pop React Router history.
 * Must run under a React Router provider. Subscribe once; read route via refs.
 */
function useCapacitorBackButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);
  navigateRef.current = navigate;
  locationRef.current = location;

  useEffect(() => {
    const handleBackButton = () => {
      const path = locationRef.current.pathname;
      if (path === "/" || path === "/login") {
        void CapacitorNativeApp.exitApp();
      } else {
        void navigateRef.current(-1);
      }
    };
    const listener = CapacitorNativeApp.addListener("backButton", handleBackButton);
    return () => {
      void listener.then((l) => l.remove());
    };
  }, []);
}

/**
 * Outer native shell: router, hardware back, safe areas, top nav, and bottom chrome
 * (TTS player / bottom nav / safe-area padding) hidden while the keyboard is open or
 * while auth is still loading / offline.
 */
export function CapacitorApp({ children }: { children: ReactNode }) {
  return (
    <BrowserRouter>
      <CapacitorAppShell>{children}</CapacitorAppShell>
    </BrowserRouter>
  );
}

function CapacitorAppShell({ children }: { children: ReactNode }) {
  useCapacitorBackButton();
  const isKeyboardVisible = useIsKeyboardVisible();
  const isLoading = useSignal(authLoadingSignal);
  const connectionError = useSignal(connectionErrorSignal);
  const showBottomChrome = !isKeyboardVisible && !isLoading && !connectionError;

  return (
    <div className={appStyles}>
      <div className={safeAreaTopStyles} />
      {children}
      {showBottomChrome && (
        <>
          <TtsPlayer />
          <Navbar />
          <div className={safeAreaBottomStyles} />
        </>
      )}
    </div>
  );
}
